import { clsx } from 'clsx';
import * as React from 'react';

import { FieldError } from '@/vibes/soul/form/field-error';
import { Label } from '@/vibes/soul/form/label';

/**
 * This component supports various CSS variables for theming. Here's a comprehensive list, along
 * with their default values:
 *
 * ```css
 *  :root {
 *    --textarea-light-background: hsl(var(--background));
 *    --textarea-light-text: hsl(var(--foreground));
 *    --textarea-light-placeholder: hsl(var(--contrast-500));
 *    --textarea-light-border: hsl(var(--contrast-100));
 *    --textarea-light-border-focus: hsl(var(--foreground));
 *    --textarea-light-border-error: hsl(var(--error));
 *    --textarea-dark-background: hsl(var(--foreground));
 *    --textarea-dark-text: hsl(var(--background));
 *    --textarea-dark-placeholder: hsl(var(--contrast-100));
 *    --textarea-dark-border: hsl(var(--contrast-500));
 *    --textarea-dark-border-focus: hsl(var(--background));
 *    --textarea-dark-border-error: hsl(var(--error));
 *  }
 * ```
 */
export const Textarea = React.forwardRef<
  React.ComponentRef<'textarea'>,
  React.ComponentPropsWithoutRef<'textarea'> & {
    prepend?: React.ReactNode;
    label?: string;
    errors?: string[];
    colorScheme?: 'light' | 'dark';
  }
>(({ label, className, errors, colorScheme = 'light', id: propId, ...rest }, ref) => {
  const generatedId = React.useId();
  const id = propId ?? generatedId;
  const errorId = `${id}-error`;
  const hasErrors = errors && errors.length > 0;

  return (
    <div className={clsx('space-y-2', className)}>
      {label != null && label !== '' && (
        <Label colorScheme={colorScheme} htmlFor={id}>
          {label}
        </Label>
      )}
      <textarea
        {...rest}
        aria-describedby={hasErrors ? errorId : undefined}
        aria-invalid={hasErrors || undefined}
        className={clsx(
          'w-full rounded-lg border p-3 transition-colors duration-200 placeholder:font-normal focus:outline-hidden disabled:cursor-not-allowed disabled:opacity-50',
          {
            light:
              'bg-(--textarea-light-background,hsl(var(--background))) text-(--textarea-light-text,hsl(var(--foreground))) placeholder-(--textarea-light-placeholder,hsl(var(--contrast-500))) focus:border-(--textarea-light-border-focus,hsl(var(--foreground)))',
            dark: 'bg-(--textarea-dark-background,hsl(var(--foreground))) text-(--textarea-dark-text,hsl(var(--background))) placeholder-(--textarea-dark-placeholder,hsl(var(--contrast-100))) focus:border-(--textarea-dark-border-focus,hsl(var(--background)))',
          }[colorScheme],
          {
            light:
              hasErrors
                ? 'border-(--textarea-light-border-error,hsl(var(--error)))'
                : 'border-(--textarea-light-border,hsl(var(--contrast-100)))',
            dark:
              hasErrors
                ? 'border-(--textarea-dark-border-error,hsl(var(--error)))'
                : 'border-(--textarea-dark-border,hsl(var(--contrast-500)))',
          }[colorScheme],
        )}
        id={id}
        ref={ref}
      />
      {hasErrors && (
        <div id={errorId}>
          {errors.map((error) => (
            <FieldError key={error}>{error}</FieldError>
          ))}
        </div>
      )}
    </div>
  );
});

Textarea.displayName = 'Textarea';
